import { Utils, StringUtils, ArrayUtils, Logger, engineComponent } from "neo-common";
import { ShapeElement } from "./ShapeElement";
import { ComponentTypes } from "../../ComponentTypes";
import { StageController } from "../../StageController";
import { Tweens, TweensAttributes } from "../../Effects/Tweens";
import { IElement } from "../Interfaces/IElement";
import { Attributes } from "../../../Utils/Attributes";
import { PathElement } from "../../../CustomComponents/PathElement";
import { EllipseElement } from "../../../CustomComponents/EllipseElement";
import { PolygonElement } from "../../../CustomComponents/PolygonElement";
import { ImageElement } from "./ImageElement";

/**
 * BaseElement class is class which creates holder for other display objects.
 * Also handle all basic properties of display object as X, Y, Rotation, Tween, Scale, Visible, Mask.
 * @class BaseElement
 * @extends PIXI.Container
 */
@engineComponent(ComponentTypes.DisplayObject)
export class BaseElement extends PIXI.Container implements IElement {
    /**
     * Unique component identifier
     */
    private _uid: string;
    private _cloned: boolean;
    private __destroyed: boolean;

    maskSprite: PIXI.Sprite;

    static _destroyOptions: PIXI.DestroyOptions = {
        children: true,
    };

    private _tweenEngine: string;

    private _positionTweenInitialized: boolean;
    public positionTweenX: Array<number>;
    public positionTweenY: Array<number>;
    public positionTweenEase: string;
    public positionTweenPosition: string;
    public positionTweenDuration: number;
    public positionTweenDelay: number;

    private _scaleTweenInitialized: boolean;
    public scaleTweenX: Array<number>;
    public scaleTweenY: Array<number>;
    public scaleTween: Array<PIXI.Point>;
    public scaleTweenEase: string;
    public scaleTweenDuration: number;
    public scaleTweenDelay: number;

    private _rotationTweenInitialized: boolean;
    public rotationTween: Array<number>;
    public rotationTweenEase: string;
    public rotationTweenDuration: number;
    public rotationTweenDelay: number;

    private _alphaTweenInitialized: boolean;
    public alphaTween: Array<number>;
    public alphaTweenEase: string;
    public alphaTweenDuration: number;
    public alphaTweenDelay: number;

    constructor() {
        super();

        this._cloned = false;
        this.__destroyed = false;

        this._uid = `${this.componentName}_${Utils.generateUuid()}`;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                this._uid = PerfDebug.generateElementUID(this);
                PerfDebug.newElement(this, this.getDebugInfo());
            }
        }
    }

    getDebugInfo(): string {
        if (DEBUG) {
            let debugInfoFn = (this as any).debugInfo;
            return debugInfoFn ? debugInfoFn.call(this) : `${this.uid}\nName: [${this.name}]`;
        }
        return "";
    }

    get tweenEngine(): string {
        return this._tweenEngine;
    }

    set tweenEngine(engine: string) {
        this._tweenEngine = engine;
    }

    /**
     * @property cloned - indicates if element was cloned or not.
     * @returns {boolean}
     */
    public get cloned(): boolean {
        return this._cloned;
    }

    public get destroyed(): boolean {
        return this.__destroyed;
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.assertDestroyed("destroy");

        if (Utils.isNullOrUndefined(options)) {
            options = BaseElement._destroyOptions;
        }

        this.internalDestroy(options);

        if (!Utils.isNullOrUndefined(this.children)) {
            super.destroy(options);
        }

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.destroy(this);
            }
        }

        this.__destroyed = true;
    }

    /**
     * todo Destroy tweens by used tween engine
     * @method internalDestroy - cleanup resources
     * @param options
     */
    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this);
    }

    public clone(): any {
        throw new Error("Not implemented");
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    protected removeAndDestroyChildren(): void {
        this._removeAndDestroyChildren();
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    _removeAndDestroyChildren(): void {
        this.assertDestroyed("_removeAndDestroyChildren");

        let destroyOptions: PIXI.DestroyOptions = {
            children: true,
        };

        if (this.children != null && this.children.length > 0) {
            for (let i = this.children.length - 1; i >= 0; i--) {
                if (this.children[i] != null) {
                    (this.children[i] as any).destroy(destroyOptions);
                }
            }
        }

        this.removeChildren();
    }

    protected assertDestroyed(method: string): void {
        if (DEBUG) {
            if (this.__destroyed) {
                throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
            }
        }
    }

    /**
     * Marks element as cloned
     */
    protected markAsCloned(): void {
        this._markAsCloned();
    }

    /**
     * Marks element as cloned
     */
    _markAsCloned(): void {
        this.assertDestroyed("markAsCloned()");
        this._cloned = true;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.clone(this);

                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    // for ProtonElement.ts (to avoid recursive calls of .create)
    public _create(node: Element): void {
        this.createByNode(node);
        this.updateByNode(node);
    }

    public create(node: Element): void {
        this._create(node);
    }

    /**
     * todo Destroy tweens by used tween engine
     *
     * @method _create - setup element from xml node
     * @param node
     * @private
     */
    public createByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(BaseElementAttributes.DEBUG) &&
                node.getAttribute(BaseElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        this.assertDestroyed("_create()");

        if (this.name == null) {
            this.name = node.nodeName;
        }

        this.alpha = Attributes.assignFloatAttribute(node, BaseElementAttributes.ALPHA, this.alpha);

        if (node.hasAttribute(BaseElementAttributes.ROTATION)) {
            var values: Array<number> = node
                .getAttribute(BaseElementAttributes.ROTATION)
                .split(";")
                .map((value) => parseFloat(value));
            if (values.length > 1) {
                this.rotation = this.degrees2radians(values[0] + Math.random() * (values[1] - values[0]));
            } else {
                this.rotation = this.degrees2radians(values[0]);
            }
        }

        if (node.hasAttribute(BaseElementAttributes.START_ROTATION)) {
            if (node.getAttribute(BaseElementAttributes.START_ROTATION) === BaseElementAttributes.START_ROTATION) {
                //this.rotation = Math.floor(Math.random() * 360);
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        if (node.hasAttribute(BaseElementAttributes.SKEW_X)) {
            this.skew.x = this.degrees2radians(parseFloat(node.getAttribute(BaseElementAttributes.SKEW_X)));
        }

        if (node.hasAttribute(BaseElementAttributes.SKEW_Y)) {
            this.skew.y = this.degrees2radians(parseFloat(node.getAttribute(BaseElementAttributes.SKEW_Y)));
        }

        /* todo Move scale.x && scale.y into SCALE attribute condition ? */
        this.scale.x = Attributes.assignFloatAttribute(node, BaseElementAttributes.SCALE_X, this.scale.x);
        this.scale.y = Attributes.assignFloatAttribute(node, BaseElementAttributes.SCALE_Y, this.scale.y);

        if (node.hasAttribute(BaseElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(BaseElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(BaseElementAttributes.SCALE));
        }

        this.position.x = Attributes.assignIntAttribute(node, BaseElementAttributes.X, this.position.x);
        this.position.y = Attributes.assignIntAttribute(node, BaseElementAttributes.Y, this.position.y);

        this.pivot.x = Attributes.assignIntAttribute(node, BaseElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, BaseElementAttributes.PIVOT_Y, this.pivot.y);
        this.visible = Attributes.assignBooleanAttribute(node, BaseElementAttributes.VISIBLE, this.visible);

        if (node.hasAttribute(BaseElementAttributes.MASK)) {
            let next: Element = node.parentElement.firstElementChild;
            while (next != null) {
                if (next.nodeName === node.getAttribute(BaseElementAttributes.MASK)) {
                    let sElement: ShapeElement | ImageElement;
                    switch (next.getAttribute("Class")) {
                        case "EllipseElement":
                            sElement = new EllipseElement();
                            break;
                        case "PolygonElement":
                            sElement = new PolygonElement();
                            break;
                        case "PathElement":
                            sElement = new PathElement();
                            break;
                        case "ImageElement":
                            sElement = new ImageElement();
                            break;
                        default:
                            sElement = new ShapeElement();
                            break;
                    }
                    sElement.create(next);
                    this.mask = sElement;
                    this.addChild(this.mask);
                }
                next = next.nextElementSibling;
            }

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(BaseElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(BaseElementAttributes.BITMAP_MASK)) {
            let image = StageController.getTextureByResourceId(node.getAttribute(BaseElementAttributes.BITMAP_MASK));

            if (image != null) {
                this.maskSprite = new PIXI.Sprite(image);
                if (node.hasAttribute(BaseElementAttributes.BITMAP_MASK_X)) {
                    this.maskSprite.position.x = parseFloat(node.getAttribute(BaseElementAttributes.BITMAP_MASK_X));
                }
                if (node.hasAttribute(BaseElementAttributes.BITMAP_MASK_Y)) {
                    this.maskSprite.position.y = parseFloat(node.getAttribute(BaseElementAttributes.BITMAP_MASK_Y));
                }
                if (!node.hasAttribute(BaseElementAttributes.BITMAP_MASK_SHOW)) {
                    this.addChild(this.maskSprite);
                    this.mask = this.maskSprite;
                }
                this.maskSprite.scale.x = node.hasAttribute(BaseElementAttributes.BITMAP_MASK_SCALE_X)
                    ? parseFloat(node.getAttribute(BaseElementAttributes.BITMAP_MASK_SCALE_X))
                    : node.hasAttribute(BaseElementAttributes.BITMAP_MASK_SCALE)
                      ? parseFloat(node.getAttribute(BaseElementAttributes.BITMAP_MASK_SCALE))
                      : 1;
                this.maskSprite.scale.y = node.hasAttribute(BaseElementAttributes.BITMAP_MASK_SCALE_Y)
                    ? parseFloat(node.getAttribute(BaseElementAttributes.BITMAP_MASK_SCALE_Y))
                    : node.hasAttribute(BaseElementAttributes.BITMAP_MASK_SCALE)
                      ? parseFloat(node.getAttribute(BaseElementAttributes.BITMAP_MASK_SCALE))
                      : 1;
            } else {
                if (DEBUG) {
                    this.log(
                        "ERROR" +
                            this.componentName +
                            " no image with name for mask: " +
                            node.getAttribute(BaseElementAttributes.BITMAP_MASK),
                    );
                }
            }
        }

        if (node.hasAttribute(BaseElementAttributes.TWEEN_ENGINE)) {
            this._tweenEngine = node.getAttribute(BaseElementAttributes.TWEEN_ENGINE);
        }

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.createElement(this, this.getDebugInfo());
            }
        }
    }

    /**
     * @param node
     */
    public updateByNode(node: Element): void {
        if (!node) {
            this.logError("No node specified for element update!");
            return;
        }

        if (DEBUG) {
            if (
                node.hasAttribute(BaseElementAttributes.DEBUG) &&
                node.getAttribute(BaseElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        if (
            !node.hasAttribute(BaseElementAttributes.TWEEN_PERSISTENT) ||
            StringUtils.parseBoolean(node.getAttribute(BaseElementAttributes.TWEEN_PERSISTENT)) != true
        ) {
            Tweens.removeTweens(this);
        }

        this.visible = Attributes.assignBooleanAttribute(node, BaseElementAttributes.VISIBLE, this.visible);
        this.alpha = Attributes.assignFloatAttribute(node, BaseElementAttributes.ALPHA, this.alpha);
        this.pivot.x = Attributes.assignIntAttribute(node, BaseElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, BaseElementAttributes.PIVOT_Y, this.pivot.y);

        if (node.hasAttribute(BaseElementAttributes.ROTATION)) {
            this.rotation = this.degrees2radians(parseFloat(node.getAttribute(BaseElementAttributes.ROTATION)));
        }

        if (node.hasAttribute(BaseElementAttributes.START_ROTATION)) {
            if (node.getAttribute(BaseElementAttributes.START_ROTATION) === BaseElementAttributes.START_ROTATION) {
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        this.position.x = Attributes.assignIntAttribute(node, BaseElementAttributes.X, this.position.x);
        this.position.y = Attributes.assignIntAttribute(node, BaseElementAttributes.Y, this.position.y);

        if (node.hasAttribute(BaseElementAttributes.SKEW_X)) {
            this.skew.x = this.degrees2radians(parseFloat(node.getAttribute(BaseElementAttributes.SKEW_X)));
        }

        if (node.hasAttribute(BaseElementAttributes.SKEW_Y)) {
            this.skew.y = this.degrees2radians(parseFloat(node.getAttribute(BaseElementAttributes.SKEW_Y)));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(BaseElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(BaseElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(BaseElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(BaseElementAttributes.SCALE));
        }

        this.runAlphaTween(node);
        this.runPositionTween(node);
        this.runRotationTween(node);
        this.runScaleTween(node);
        this.runSkewTween(node);
        this.runTween(node);

        this.updateByNodeInherited(node); // for ProtonElement updating
    }

    private updateByNodeInherited(node: Element): void {}

    /**
     * Runs the alpha tween
     * @method runAlphaTween
     * @param node
     * @returns {boolean}
     */
    public runAlphaTween(node: Element): boolean {
        if (!node.hasAttribute(BaseElementAttributes.ALPHA_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this, "alpha");

        let alphaTween: Array<number>;

        let alphaTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.ALPHA_TWEEN_POSITION,
            null,
        );

        if (alphaTweenPosition == BaseElementAttributes.ALPHA_TWEEN_POSITION_VALUE.RELATIVE) {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ALPHA_TWEEN)).map(
                (value) => value + this.alpha,
            );
        } else if (alphaTweenPosition == BaseElementAttributes.ALPHA_TWEEN_POSITION_VALUE.SMOOTH) {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ALPHA_TWEEN));
            if (alphaTween.length) {
                alphaTween[0] = this.alpha;
            }
        } else {
            alphaTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ALPHA_TWEEN));
        }

        const alphas: Array<number> = alphaTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(node, BaseElementAttributes.ALPHA_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.ALPHA_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.ALPHA_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.ALPHA_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.ALPHA_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.ALPHA_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ALPHA_TWEEN_KEY_TIMES))
            : null;

        Tweens.alphaTween(this, { alphas, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the position tween
     * @method runPositionTween
     * @param node
     * @returns {boolean}
     */
    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_X) &&
            !node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        Tweens.removeTweens(this, "position");

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_POSITION,
            null,
        );
        if (positionTweenPosition == BaseElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            if (!node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_X)) {
                positionTweenX = [];
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => {
                        positionTweenX.push(this.position.x);
                        return value + this.position.y;
                    },
                );
            } else if (!node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_Y)) {
                positionTweenY = [];
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => {
                        positionTweenY.push(this.position.y);
                        return value + this.position.x;
                    },
                );
            } else {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => value + this.position.x,
                );
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => value + this.position.y,
                );
            }
        } else {
            if (!node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_X)) {
                positionTweenX = [];
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_Y)).map(
                    (value) => {
                        positionTweenX.push(this.position.x);
                        return value;
                    },
                );
            } else if (!node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_Y)) {
                positionTweenY = [];
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_X)).map(
                    (value) => {
                        positionTweenY.push(this.position.y);
                        return value;
                    },
                );
            } else {
                positionTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_X));
                positionTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_Y));
            }
            if (positionTweenPosition == BaseElementAttributes.POSITION_TWEEN_POSITION_VALUE.SMOOTH) {
                if (positionTweenX.length) {
                    positionTweenX[0] = this.position.x;
                }
                if (positionTweenY.length) {
                    positionTweenY[0] = this.position.y;
                }
            }
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.POSITION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_KEY_TIMES))
            : null;

        let bezierType: string;
        let bezierCurviness: number;
        let bezierPoints: Object;
        let bezierObject: Object;
        let bezierAutoRotate: boolean;

        if (node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_BEZIER_POINTS)) {
            bezierType = Attributes.assignStringAttribute(
                node,
                BaseElementAttributes.POSITION_TWEEN_BEZIER_TYPE,
                "thru",
            );
            bezierCurviness = Attributes.assignIntAttribute(
                node,
                BaseElementAttributes.POSITION_TWEEN_BEZIER_CURVINESS,
                1.0,
            );
            bezierPoints = this.formatPoints(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_BEZIER_POINTS));
            bezierAutoRotate = Attributes.assignBooleanAttribute(
                node,
                BaseElementAttributes.POSITION_TWEEN_BEZIER_AUTOROTATE,
                false,
            );

            if (bezierType && bezierCurviness && bezierPoints) {
                bezierObject = {
                    type: bezierType,
                    curviness: bezierCurviness,
                    values: bezierPoints,
                    autoRotate: bezierAutoRotate ? ["x", "y", "rotation", Math.PI / 2, true] : false,
                };
            }
        }

        Tweens.positionTween(this, {
            bezier: bezierObject,
            tweenX,
            tweenY,
            tweenEase,
            tweenDuration,
            tweenDelay,
            tweenLoop,
            keyTimes,
        });
    }

    /**
     *
     * @param {string} value
     * @param {string} id
     * @param {boolean} useGlobal
     * @returns {number}
     */
    protected formatPoint(value: string, id: string, useGlobal: boolean = false): number {
        let result: number;
        if (value == "c") {
            result = this[id];
        } else {
            if (useGlobal) {
                result = this[id] + (parseInt(value) - this.getGlobalPosition()[id]);
            } else {
                result = parseInt(value);
            }
        }

        return result;
    }

    /**
     *
     * @param {string} points
     * @param {boolean} useGlobal
     * @returns {Array<PIXI.Point>}
     */
    protected formatPoints(points: string, useGlobal: boolean = false): Array<Object> {
        let result: Array<Object> = [];
        let regex = /\[(.*?)\]/g;
        let data = points.match(regex);

        for (let i: number = 0; i < data.length; i++) {
            let pointArray = data[i].substring(1, data[i].length - 1).split(",");

            if (pointArray.length == 2) {
                let x = this.formatPoint(pointArray[0], "x", useGlobal);
                let y = this.formatPoint(pointArray[1], "y", useGlobal);

                //let point: PIXI.Point = new PIXI.Point(x, y);
                let objectPoint: Object = { x: x, y: y };
                result.push(objectPoint);
            }
        }

        return result;
    }

    public runSkewTween(node: Element): boolean {
        if (
            !node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_X) &&
            !node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_Y)
        ) {
            return false;
        }

        Tweens.removeTweens(this, "skew");

        let skewTweenX: Array<number>;
        let skewTweenY: Array<number>;

        let skewTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.SKEW_TWEEN_POSITION,
            null,
        );
        if (skewTweenPosition == BaseElementAttributes.SKEW_TWEEN_POSITION_VALUE.RELATIVE) {
            skewTweenX = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_X)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_X)).map(
                      (value) => Tweens.degrees2radians(value) + this.skew.x,
                  )
                : null;
            skewTweenY = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_Y)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_Y)).map(
                      (value) => Tweens.degrees2radians(value) + this.skew.y,
                  )
                : null;
        } else if (skewTweenPosition == BaseElementAttributes.SKEW_TWEEN_POSITION_VALUE.SMOOTH) {
            skewTweenX = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_X)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_X)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
            skewTweenY = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_Y)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_Y)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
            if (skewTweenX && skewTweenX.length) {
                skewTweenX[0] = this.skew.x;
            }
            if (skewTweenY && skewTweenY.length) {
                skewTweenY[0] = this.skew.y;
            }
        } else {
            skewTweenX = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_X)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_X)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
            skewTweenY = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_Y)
                ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_Y)).map((value) =>
                      Tweens.degrees2radians(value),
                  )
                : null;
        }

        const tweenX: Array<number> = skewTweenX || [];
        const tweenY: Array<number> = skewTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(node, BaseElementAttributes.SKEW_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.SKEW_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.SKEW_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(node, BaseElementAttributes.SKEW_TWEEN_LOOP, null);
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.SKEW_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SKEW_TWEEN_KEY_TIMES))
            : null;

        Tweens.skewTween(this, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the rotation tween
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runRotationTween(node: Element): boolean {
        if (!node.hasAttribute(BaseElementAttributes.ROTATION_TWEEN)) {
            return false;
        }

        Tweens.removeTweens(this, "rotation");

        let rotationTween: Array<number>;

        let rotationTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_POSITION,
            null,
        );
        if (rotationTweenPosition == BaseElementAttributes.ROTATION_TWEEN_POSITION_VALUE.RELATIVE) {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ROTATION_TWEEN)).map(
                (value) => value + Tweens.radians2degrees(this.rotation),
            );
        } else if (rotationTweenPosition == BaseElementAttributes.ROTATION_TWEEN_POSITION_VALUE.SMOOTH) {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ROTATION_TWEEN));
            if (rotationTween.length) {
                rotationTween[0] = Tweens.radians2degrees(this.rotation);
            }
        } else {
            rotationTween = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ROTATION_TWEEN));
        }

        const degrees: Array<number> = rotationTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.ROTATION_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.ROTATION_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.ROTATION_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ROTATION_TWEEN_KEY_TIMES))
            : null;

        Tweens.rotationTween(this, { degrees, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    /**
     * Runs the scale tween
     *
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runScaleTween(node: Element): boolean {
        if (
            !node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_X) &&
            !node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_Y) &&
            !node.hasAttribute(BaseElementAttributes.SCALE_TWEEN)
        ) {
            return false;
        }

        Tweens.removeTweens(this, "scale");

        let scaleTweenX: Array<number>;
        let scaleTweenY: Array<number>;
        let scaleTween: Array<PIXI.Point>;
        let tweenValues: Array<PIXI.Point> = [];

        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_X)) {
            scaleTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SCALE_TWEEN_X));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_Y)) {
            scaleTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SCALE_TWEEN_Y));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN)) {
            scaleTween = BaseElement._getPointsFromPointsAttribute(
                node.getAttribute(BaseElementAttributes.SCALE_TWEEN),
            ) as Array<PIXI.Point>;
        }

        let scaleTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.SCALE_TWEEN_POSITION,
            null,
        );

        if (scaleTween && scaleTween.length) {
            if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTween.map((value: PIXI.Point) => {
                    return {
                        x: value.x + this.scale.x,
                        y: value.y + this.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTween;
                if (tweenValues.length) {
                    tweenValues[0].x = this.scale.x;
                    tweenValues[0].y = this.scale.y;
                }
            } else {
                tweenValues = scaleTween;
            }
        } else if (scaleTweenX && scaleTweenY) {
            if (scaleTweenX.length === scaleTweenY.length) {
                if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue + this.scale.x,
                            y: scaleTweenY[i] + this.scale.y,
                        } as PIXI.Point;
                    });
                } else if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue,
                            y: scaleTweenY[i],
                        } as PIXI.Point;
                    });
                    if (tweenValues.length) {
                        tweenValues[0].x = this.scale.x;
                        tweenValues[0].y = this.scale.y;
                    }
                } else {
                    tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                        return {
                            x: scaleXValue,
                            y: scaleTweenY[i],
                        } as PIXI.Point;
                    });
                }
            }
        } else if (scaleTweenX) {
            if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue + this.scale.x,
                        y: this.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: this.scale.y,
                    } as PIXI.Point;
                });
                if (tweenValues.length) {
                    tweenValues[0].x = this.scale.x;
                }
            } else {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: this.scale.y,
                    } as PIXI.Point;
                });
            }
        } else if (scaleTweenY) {
            if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.RELATIVE) {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.scale.x,
                        y: scaleYValue + this.scale.y,
                    } as PIXI.Point;
                });
            } else if (scaleTweenPosition == BaseElementAttributes.SCALE_TWEEN_POSITION_VALUE.SMOOTH) {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.scale.x,
                        y: scaleYValue,
                    } as PIXI.Point;
                });
                if (tweenValues.length) {
                    tweenValues[0].y = this.scale.y;
                }
            } else {
                tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                    return {
                        x: this.scale.x,
                        y: scaleYValue,
                    } as PIXI.Point;
                });
            }
        } else {
            return false;
        }

        const tweenEase: string = Attributes.assignStringAttribute(node, BaseElementAttributes.SCALE_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.SCALE_TWEEN_DURATION,
            120,
        );
        let tweenDelay: number = 0;
        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_DELAY)) {
            let tweenDelayArray: Array<number> = node
                .getAttribute(BaseElementAttributes.SCALE_TWEEN_DELAY)
                .split(";")
                .map((value) => parseInt(value));
            tweenDelay =
                tweenDelayArray.length == 2
                    ? Math.round(tweenDelayArray[0] + Math.random() * (tweenDelayArray[1] - tweenDelayArray[0]))
                    : tweenDelayArray[0];
        }
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.SCALE_TWEEN_LOOP,
            null,
        );
        const keyTimes: Array<number> = node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_KEY_TIMES)
            ? ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SCALE_TWEEN_KEY_TIMES))
            : null;

        Tweens.scaleTween(this, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop, keyTimes });
    }

    public runTween(node: Element): boolean {
        if (node.hasAttribute(TweensAttributes.TWEEN)) {
            let tweens: Array<string> = ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN));

            for (let i: number = 0; i < tweens.length; i++) {
                //return if there are no values to tween
                if (!node.hasAttribute(TweensAttributes.TWEEN_VALUES + i)) {
                    return false;
                }

                let tween: string = tweens[i];

                const tweenValues: Array<number> = this.formatTweenValues(
                    tween,
                    ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN_VALUES + i)),
                );
                const tweenDuration: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DURATION + i) || "120",
                );
                const tweenEase: Array<string> = ArrayUtils.stringArray(
                    node.getAttribute(TweensAttributes.TWEEN_EASE + i) || "linear",
                );
                const tweenDelay: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DELAY + i) || "0",
                );
                const tweenLoop: boolean = Attributes.assignBooleanAttribute(
                    node,
                    TweensAttributes.TWEEN_LOOP + i,
                    null,
                );

                Tweens.removeTweens(this, tween);

                if (tween == "scale") {
                    tween = "scale.x";
                    tweens[i] = "scale.y";
                    i--;
                }

                Tweens.customTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            }
        }

        return false;
    }

    private formatTweenValues(tween: string, values: Array<string>): Array<number> {
        let tweenValues: Array<number> = [];

        if (tween === "scale") {
            tween = "scale.x";
        }
        let property: Array<string> = tween.split(".");
        let value: number;

        for (let i: number = 0; i < values.length; i++) {
            let current = values[i];
            switch (current[current.length - 1]) {
                case "c": //if we want current value
                    if (property.length == 1) {
                        value = this[property[0]];
                        if (property[0] === "rotation") {
                            value = this.radians2degrees(value);
                        }
                        tweenValues.push(value);
                    } else if (property.length == 2) {
                        tweenValues.push(this[property[0]][property[1]]);
                    }

                    break;
                case "+":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] + value;
                    }
                    tweenValues.push(value);
                    break;
                case "-":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] - value;
                    }
                    tweenValues.push(value);
                    break;
                default:
                    tweenValues.push(parseFloat(current));
            }
        }

        return tweenValues;
    }

    /**
     * Converts string from "points attribute" into array of points. E.g. "(1,6);(5,2);2;(2,4)". Point can be specified as single value for both axex.
     * @method _getPointsFromPointsAttribute
     * @return Array<PIXI.Points>
     */
    protected static _getPointsFromPointsAttribute(pointsString: string): Array<{ x: number; y: number }> {
        if (!pointsString) {
            return [];
        }

        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) =>
                /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair],
            )
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public radians2degrees(radians: number): number {
        return radians * (180 / Math.PI);
    }
}

export const BaseElementAttributes = {
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",

    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    ALPHA_TWEEN_KEY_TIMES: "AlphaTweenKeyTimes",
    ALPHA_TWEEN_POSITION: "AlphaTweenPosition",
    ALPHA_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    /*
     * A ImageElement instance that defines a mask for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property BitmapMask
     * @type string
     * @default null
     */
    BITMAP_MASK: "BitmapMask",
    BITMAP_MASK_SHOW: "BitmapMaskShow",
    BITMAP_MASK_X: "BitmapMaskX",
    BITMAP_MASK_Y: "BitmapMaskY",
    BITMAP_MASK_SCALE_X: "BitmapMaskScaleX",
    BITMAP_MASK_SCALE_Y: "BitmapMaskScaleY",
    BITMAP_MASK_SCALE: "BitmapMaskScale",
    DEBUG: "Debug",
    /*
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type ShapeElement
     * @default null
     */
    MASK: "Mask",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
        SMOOTH: "smooth",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    POSITION_TWEEN_KEY_TIMES: "PositionTweenKeyTimes",
    /**
     * Bezier's "type" of curve
     * VALUES:
     *      thru
     *      soft
     *      quadratic
     *      cubic
     * EXAMPLE:
     *      PositionTweenBezierType="thru"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenBezierType
     * @default thru
     */
    POSITION_TWEEN_BEZIER_TYPE: "PositionTweenBezierType",
    /**
     * the tension on the Bezier
     * VALUES:
     *      0 has no curviness (straight lines),
     *      1 is normal curviness,
     *      2 is twice the normal curviness,
     *      etc. Use any number, not just integers
     * EXAMPLE:
     *      PositionTweenBezierCurviness="1.25"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenBezierCurviness
     * @default 1.0
     */
    POSITION_TWEEN_BEZIER_CURVINESS: "PositionTweenBezierCurviness",
    /**
     * Array points, pairs of X,Y coords for movement along the bezier curve
     * EXAMPLE:
     *                                   x   y     x  y    x   y
     *      PositionTweenBezierPoints="[100,250];[300,0];[500,400]"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenBezierPoints
     * @default null
     */
    POSITION_TWEEN_BEZIER_POINTS: "PositionTweenBezierPoints",
    /**
     * Auto rotation object on Bezier
     * VALUES:
     *      true
     *      false
     * EXAMPLE:
     *      PositionTweenBezierAutoRotate="true"
     * WARNING:
     *      it is necessary to have 2 "X" position values and 2 "Y" for correct Bezier function
     *      for example, ' PositionTweenX="960;960"  PositionTweenY="540;540" '
     * @property PositionTweenBezierAutoRotate
     * @default false
     */
    POSITION_TWEEN_BEZIER_AUTOROTATE: "PositionTweenBezierAutoRotate",
    /*
     *
     */
    ROTATION_TWEEN: "RotationTween",
    /**
     * @deprecated Use ROTATION_TWEEN instead ROTATION_TWEEN_DEGREES
     */
    ROTATION_TWEEN_DEGREES: "RotationTweenDegrees",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_LOOP: "RotationTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    ROTATION_TWEEN_KEY_TIMES: "RotationTweenKeyTimes",
    ROTATION_TWEEN_POSITION: "RotationTweenPosition",
    ROTATION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    /**
     * @deprecated Use SCALE_TWEEN instead SCALE_TWEEN_DURATION
     */
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_LOOP: "ScaleTweenLoop",
    /* Array of offsets of individual tween values relative to total duration
     * e.g. 0;0.25;1 with duration 100 meaning first value at T=0, second at T=25, last at T=100
     */
    SCALE_TWEEN_KEY_TIMES: "ScaleTweenKeyTimes",
    SCALE_TWEEN_POSITION: "ScaleTweenPosition",
    SCALE_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute" /* values are taken as they are */,
        RELATIVE: "relative" /* values are offset by initial value */,
        SMOOTH: "smooth" /* first value is replaced with initial value */,
    },
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    /*
     * The factor to stretch this display object horizontally.
     * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
     * @property ScaleX
     * @type Number
     * @default 1
     */
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",

    SKEW_X: "SkewX",
    SKEW_Y: "SkewY",

    SKEW_TWEEN_X: "SkewTweenX",
    SKEW_TWEEN_Y: "SkewTweenY",
    SKEW_TWEEN_EASE: "SkewTweenEase",
    SKEW_TWEEN_DURATION: "SkewTweenDuration",
    SKEW_TWEEN_DELAY: "SkewTweenDelay",
    SKEW_TWEEN_POSITION: "SkewTweenPosition",
    SKEW_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
        SMOOTH: "smooth",
    },
    SKEW_TWEEN_LOOP: "SkewTweenLoop",
    SKEW_TWEEN_KEY_TIMES: "SkewTweenKeyTimes",

    /*
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    TWEEN_ENGINE: "TweenEngine",
    /*
     * Indicates currently running tweens should not be interrupted during updateByNode call
     * @property TweenPersistent
     * @type Boolean
     * @default false
     */
    TWEEN_PERSISTENT: "TweenPersistent",
    /*
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/BaseElement.ts
 **/